import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle, Loader2 } from "lucide-react";
import { isOfflinePlatform } from "shared";
import { Page } from "@/components/page";
import { Button } from "@/components/ui/button";
import { ImportAccountingDetailsView } from "./-components/steps/import-details";
import { SelectLiabilityAccountView } from "./-components/steps/select-liability-account";
import { AccountingProviderSelectView } from "./-components/steps/select-provider";

export const Route = createFileRoute("/_authed/_org/accounting-setup/")({
  component: RouteComponent,
  loader: async ({ context }) => {
    const accountingData = await context.queryClient.ensureQueryData(
      context.trpc.accounting.get.queryOptions(),
    );

    // If accounting integration exists, also fetch fields
    if (accountingData) {
      await context.queryClient.ensureQueryData(
        context.trpc.accounting.getAccountingFields.queryOptions(),
      );
    }
  },
});

function RouteComponent() {
  return (
    <Page heading="Accounting Setup">
      <div className="flex h-full min-h-fit w-full justify-center bg-neutral-100 @3xl/page:px-24 @lg/page:px-8 px-4 pb-12">
        <AccountingOnboarding />
      </div>
    </Page>
  );
}

function AccountingOnboarding() {
  const { trpc } = Route.useRouteContext();

  const { data: accountingData } = useSuspenseQuery(
    trpc.accounting.get.queryOptions(),
  );

  const { data: session } = useSuspenseQuery(trpc.auth.session.queryOptions());

  // Check if accounting is in the organization's features
  const hasAccountingFeature = session?.org?.features?.includes("accounting");

  if (hasAccountingFeature) {
    return <OnboardingSuccess />;
  }

  if (
    accountingData &&
    accountingData.status === "processing" &&
    isOfflinePlatform(accountingData.type)
  ) {
    return <ImportAccountingDetailsView />;
  }

  if (accountingData && accountingData.status === "ready") {
    return <SelectLiabilityAccountView />;
  }

  if (accountingData && accountingData.status === "processing") {
    return <ProcessingView />;
  }

  return <AccountingProviderSelectView />;
}

function OnboardingSuccess() {
  return (
    <div className="mx-auto max-w-2xl space-y-8 p-8">
      {/* Success Header */}
      <div className="space-y-6 text-center">
        <div className="flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="font-semibold text-3xl text-gray-900">
            You're all set!
          </h1>
          <p className="text-gray-600 text-lg">
            Your accounting integration is now active and ready to streamline
            your workflow.
          </p>
        </div>
      </div>

      <div className="flex w-full flex-row justify-center">
        <Link className="" to="/accounting">
          <Button className="flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2 font-medium text-white transition-colors">
            View Accounting Dashboard
            <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

function ProcessingView() {
  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col justify-center gap-8">
      <div className="space-y-6 text-center">
        <div className="flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="font-semibold text-3xl text-gray-900">
            Setting up your accounting integration
          </h1>
          <p className="text-gray-600 text-lg">
            We're syncing your accounting data. This typically takes 1-2
            minutes.
          </p>
          <p className="text-gray-500 text-sm">
            Feel free to refresh this page to check the progress.
          </p>
        </div>
      </div>
    </div>
  );
}
